import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type {
  Subscription,
  SubscriptionLine,
  Holiday,
  HolidayRequest,
  LineAmendment,
  LineFreeze,
  PriceListItem,
  DeliveryOverlay,
} from '@/types'
import subscriptionService from '@/services/subscriptionService'
import deliveryOverlayService from '@/services/deliveryOverlayService'
import holidayService from '@/services/holidayService'
import priceListService from '@/services/priceListService'
import { convertToCalendarHolidays, generateDeliveryDates, toStartOfDay } from '@/utils/deliverySchedule'
import {
  toLocalDateString,
  getChargebeeIdForDay,
  addDaysToDateString,
  formatOrdinalDate,
  computeOneOffEligibleDates,
  type OneOffEligibleDate,
} from '@/utils/deliveryDays'
import { formatDate } from '@/utils/dateFormat'
import { EMPTY_OVERLAY, movedDeliveryTargets, schedulePeriods } from '@/utils/deliveryOverlay'
import ScheduleChangeBanner from './ScheduleChangeBanner'
import { planSkip, planCancelSkip, type HolidayRange } from '@/utils/holidayRanges'
import { ORDER_EDITING_DISABLED } from '@/config/maintenance'
import { useAuth } from '@/hooks/useAuth'
import { useCutoffs } from '@/hooks/useCutoffs'
import * as weeklyOrder from '@/utils/weeklyOrder'
import type { ScheduledChange } from '@/utils/weeklyOrder'
import { addSchedules, billingFrequencyKey, billingFrequencyOf, frequenciesFromKey, frequencyBySubscription, mainSubscription } from '@/utils/billingFrequency'
import DeliverySchedule from './DeliverySchedule'
import DeliveryManagementPanel from './DeliveryManagementPanel'
import HolidayCalendarModal, { type HolidayDelta } from './HolidayCalendarModal'
import ChangeItemsModal from '../ChangeItemsModal'
import AddProductModal from '../AddProductModal'
import ConfirmModal from '../ConfirmModal'
import ProductDetailModal from './ProductDetailModal'
import MoveLockerModal from './MoveLockerModal'
import OrderEditingDisabledModal from '../OrderEditingDisabledModal'
import DirectDebitAlert from '../DirectDebitAlert'
import OrderBeingPreparedBanner from '../OrderBeingPreparedBanner'
import { MdAddShoppingCart, MdEdit, MdEventBusy } from 'react-icons/md'
import { mutationLockStore } from '@/services/mutationLockStore'
import '@/styles/components/sun-dashboard.css'

/**
 * sunMode dashboard.
 *
 * Two panels with one rule between them: the left is the standing order and
 * the only place anything can be changed; the right is that order resolved
 * into dated deliveries, and is read-only. Keeping the derived view
 * unerditable is what lets each side stay simple — the left never has to
 * explain when a change lands, and the right never has to offer a date picker.
 */

/** The per-day skip confirmations, on the same flag as every other date. */
const formatSkipDate = (date: Date): string => formatDate(date)

/**
 * A locker move in progress (DF-230). The kind records what pause (if any) the
 * move interrupted, so the prompt can carry it out once the locker is moved:
 *   move            just move it (the locker's own Move button)
 *   pause-day       then pause the one day (the schedule's Pause link)
 *   pause-calendar  then apply the holiday calendar's staged changes
 */
type MoveContext =
  | { kind: 'move'; line: SubscriptionLine }
  | { kind: 'pause-day'; line: SubscriptionLine; date: Date }
  | { kind: 'pause-calendar'; line: SubscriptionLine; deltas: HolidayDelta[]; excludeDates: string[] }

/**
 * Which half of the dashboard to show.
 *
 * Both views need the same subscriptions, holidays, amendments, price list
 * and every editing dialog, so they are one component rather than two pages
 * duplicating four hundred lines of handlers. The route picks a view; the
 * data loading and the modals are shared.
 */
export type SunView = 'deliveries' | 'management' | 'pause'

interface SunDashboardProps {
  view?: SunView
}

const SunDashboard: React.FC<SunDashboardProps> = ({ view = 'deliveries' }) => {
  const { user } = useAuth()
  const { cutoffs } = useCutoffs()
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  /**
   * Which days each product can be delivered on. Some products are not made
   * for every round (cream and milkshakes, for instance), and the weekly panel
   * has to tell "you could add this on Monday" apart from "Monday is not an
   * option for this product".
   */
  const [priceItems, setPriceItems] = useState<PriceListItem[]>([])
  const [overlay, setOverlay] = useState<DeliveryOverlay>(EMPTY_OVERLAY)
  /** DF-478: the special schedule periods (Christmas), for the banner. */
  const periods = useMemo(
    () => schedulePeriods(movedDeliveryTargets(overlay.round_day_amendments, overlay.date_overrides)),
    [overlay]
  )
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [holidayRequests, setHolidayRequests] = useState<HolidayRequest[]>([])
  const [amendments, setAmendments] = useState<LineAmendment[]>([])
  /** Temporary product pauses (Line Freezes, DF-350), fetched with the amendments. */
  const [freezes, setFreezes] = useState<LineFreeze[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editingProductId, setEditingProductId] = useState<string | null>(null)
  const [isAddOpen, setIsAddOpen] = useState(false)
  /** The delivery a per-day skip is being confirmed for, and which way it goes. */
  const [skipTarget, setSkipTarget] = useState<{ date: Date, mode: 'skip' | 'cancel' } | null>(null)
  const [skipping, setSkipping] = useState(false)
  /**
   * The move-a-one-off prompt (milk lockers, DF-230). One prompt serves three
   * entry points: pausing the locker's day from the schedule ('pause-day'),
   * pausing it (among others) from the holiday calendar ('pause-calendar'), and
   * the locker's own Move button ('move'). The prompt moves the locker first,
   * then carries out the pause it interrupted. `moveDate` and a busy flag ride
   * alongside.
   */
  const [moveContext, setMoveContext] = useState<MoveContext | null>(null)
  const [moveDate, setMoveDate] = useState<string>('')
  const [movingLocker, setMovingLocker] = useState(false)
  const [isEditingDisabledOpen, setIsEditingDisabledOpen] = useState(false)
  /** A scheduled change awaiting confirmation that it should be undone. */
  const [cancellingChange, setCancellingChange] = useState<ScheduledChange | null>(null)
  const [cancelling, setCancelling] = useState(false)
  /**
   * A one-off product awaiting removal confirmation. A one-off is a single
   * dated delivery, so removing it is a plain yes/no, not the day/date wizard
   * a recurring product needs (DF-286).
   */
  const [removingOneOff, setRemovingOneOff] = useState<{ productId: string, name: string } | null>(null)
  const [removingOneOffBusy, setRemovingOneOffBusy] = useState(false)
  /** The product whose detail is open, with a name to show while it loads. */
  const [viewingProduct, setViewingProduct] = useState<{ id: string, name: string, price: number } | null>(null)
  /** The edit dialog opened from the week header, starting at product choice. */
  const [isChangesOpen, setIsChangesOpen] = useState(false)
  /**
   * A change begun on a Delivery Management card (new quantity, pause or
   * remove). The card is the "what", so the dialog opens straight on the "when".
   */
  const [changeConfig, setChangeConfig] = useState<{
    productId: string
    intent: 'quantity' | 'pause' | 'remove'
    quantities?: Record<number, number>
  } | null>(null)
  /** The holiday calendar: staged skip toggles, applied together on save. */
  const [holidayCalendarOpen, setHolidayCalendarOpen] = useState(false)
  /** The month to open the calendar on, e.g. an existing skip being edited. */
  const [holidayInitialMonth, setHolidayInitialMonth] = useState<Date | null>(null)
  const [savingHolidays, setSavingHolidays] = useState(false)

  const fetchAmendments = useCallback(async (subs: Subscription[]) => {
    if (subs.length === 0) return
    try {
      const results = await Promise.all(subs.map(sub => subscriptionService.getAmendments(sub.id)))
      setAmendments(results.flat())
    } catch (err) {
      console.error('Failed to fetch amendments:', err)
    }
    try {
      const results = await Promise.all(subs.map(sub => subscriptionService.getFreezes(sub.id)))
      setFreezes(results.flat())
    } catch (err) {
      console.error('Failed to fetch paused products:', err)
    }
  }, [])

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true)
      const subs = await subscriptionService.getSubscriptions()
      setSubscriptions(subs)
      // Fetched here rather than from an effect on `subscriptions`: an effect
      // fires again after every refresh, landing once the spinner is already
      // down and redrawing the page under the customer.
      await fetchAmendments(subs)
      setError(null)
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err)
      setError('Failed to load your deliveries. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [fetchAmendments])

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await holidayService.getHolidays(50)
      setHolidayRequests(response.data)
      setHolidays(convertToCalendarHolidays(response.data))
    } catch (err) {
      // Holidays are supporting detail; the order still renders without them.
      console.error('Failed to fetch holidays:', err)
    }
  }, [])


  // Every frequency the customer's subscriptions run at, as a stable key: the
  // price list is only used here to look up products they already have, and
  // a fortnightly product is only in the fortnightly list (DF-379).
  const frequencyKey = useMemo(() => billingFrequencyKey(subscriptions), [subscriptions])

  const fetchPriceItems = useCallback(async () => {
    try {
      setPriceItems(await priceListService.getPriceListItemsForFrequencies(frequenciesFromKey(frequencyKey)))
    } catch (err) {
      // Only affects whether a day the product can't reach shows as a 0 or as
      // a blank, so the order still renders without it.
      console.error('Failed to fetch the price list:', err)
    }
  }, [frequencyKey])

  useEffect(() => {
    fetchSubscriptions()
    fetchHolidays()
    // Christmas overlays (DF-345); supporting detail, so a failure just leaves
    // the schedule without the annotations.
    deliveryOverlayService.getOverlay().then(setOverlay).catch(() => undefined)
  }, [fetchSubscriptions, fetchHolidays])

  // Refetched only when the customer's set of frequencies changes.
  useEffect(() => {
    fetchPriceItems()
  }, [fetchPriceItems])

  /**
   * Reload everything a save could have changed.
   *
   * Held under the mutation lock, because the save is not finished from the
   * customer's point of view until the screen shows the result. apiClient
   * releases the lock the moment the HTTP call resolves, so without this the
   * overlay said "Saved" and disappeared while the refresh poll was still
   * running, and the page then visibly rebuilt itself underneath them.
   *
   * apiClient's succeed() lands before this runs, so the store does pass
   * briefly through "success" on the way back to pending. React batches the
   * two updates, so it is not normally painted, and a single frame of "Saved"
   * before "Updating your deliveries" would be harmless if it were. Holding
   * the lock from before the save instead would need every call site to own
   * it, rather than the one place they all already funnel through.
   */
  const handleSuccess = useCallback(async () => {
    mutationLockStore.begin('Updating your deliveries...')

    try {
      const subs = await subscriptionService.refreshSubscriptionsUntilSettled()
      setSubscriptions(subs)
      await fetchAmendments(subs)
      await fetchHolidays()
      mutationLockStore.succeed('Saved')
    } catch (err) {
      // The change itself went through; only the refresh did not. Saying
      // "could not save" here would be a lie that sends them to make it twice.
      console.error('Failed to refresh after saving:', err)
      mutationLockStore.fail('Saved, but we could not refresh the page. Please reload.')
    }
  }, [fetchAmendments, fetchHolidays])

  // Six months, uncapped. The schedule's calendar can page forward a month at a
  // time, so it needs more than the two months on screen; the defaults (8
  // weeks, first 8 deliveries) work out at about four weeks for a two-day
  // round. Projection is pure client-side arithmetic over the standing order,
  // so the horizon costs nothing but is capped for sanity.
  // A brand-new customer whose first delivery is still being set up: show a
  // welcome note and lock that first delivery from edits (FEDEV-544). Per
  // subscription, so an already-running sub is unaffected by a newer one that
  // hasn't started yet.
  //
  // The welcome banner is only for a genuinely new customer: one with a pending
  // subscription and no active (already-delivering) one. An existing customer
  // who has a new future-dated sub added (e.g. a 2-weekly product added in CRM
  // alongside their weekly delivery) is not new, so they don't see it. The
  // first-delivery lock below still applies per pending sub regardless.
  const awaitingFirstDelivery = useMemo(
    () => subscriptions.some(sub => sub.is_awaiting_first_delivery)
      && ! subscriptions.some(sub => Number(sub.statuscode) === 1),
    [subscriptions]
  )
  const lockedDeliveryDates = useMemo(() => {
    const dates = new Set<string>()
    subscriptions.forEach(sub => {
      if (sub.is_awaiting_first_delivery && sub.first_delivery_date) {
        dates.add(sub.first_delivery_date)
      }
    })
    return dates
  }, [subscriptions])

  const deliveries = useMemo(
    () => generateDeliveryDates(subscriptions, holidays, amendments, user?.delivery_days ?? [], 26, 400, lockedDeliveryDates, freezes),
    [subscriptions, holidays, amendments, user?.delivery_days, lockedDeliveryDates, freezes]
  )

  /** The days a given product can be delivered on, for the detail dialog. */
  const deliverableDaysFor = useCallback((productId: string): number[] | undefined => {
    const item = priceItems.find(p => p.product_id === productId)
    if (!item) return undefined

    return (user?.delivery_days ?? []).filter(day => getChargebeeIdForDay(item, day) !== null)
  }, [priceItems, user?.delivery_days])

  /**
   * The scheduled changes, computed here as well as in the panel so the edit
   * dialog can show the same sentences for the product being edited. Same
   * builder either side, so the two cannot describe a change differently.
   */
  const changesByProduct = useMemo(() => {
    const relevant = weeklyOrder.relevantLines(subscriptions)

    return weeklyOrder.changesByProduct(weeklyOrder.rows(relevant, amendments), amendments, freezes)
  }, [subscriptions, amendments, freezes])

  /** Every live line, which the dialogs need to prefill and to block duplicates. */
  const allLines = useMemo<SubscriptionLine[]>(
    () => subscriptions.filter(s => s.statecode !== 1).flatMap(s => s.lines.filter(l => l.statecode !== 1)),
    [subscriptions]
  )

  /** The weekly delivery surcharge (hidden SKU-999 product line). Shown under
   *  the My Deliveries weekly total, not as a product (DF-88). */
  const surchargeAmount = useMemo<number>(
    () => allLines
      .filter(l => l.is_surcharge)
      .reduce((sum, l) => sum + l.quantity * (l.unit_price_inc_vat ?? l.unit_price ?? 0), 0),
    [allLines]
  )

  /** One-off product lines (milk lockers, DF-230): shown in their own box, not
   *  among the day-by-day products, on both My Deliveries and Delivery
   *  Management. Excludes any already delivered (ended). */
  const oneOffLines = useMemo<SubscriptionLine[]>(
    () => allLines.filter(l => l.is_one_off && !l.is_ended),
    [allLines]
  )

  /** Each subscription's frequency, so My Deliveries can tag non-weekly products (DF-392). */
  const subscriptionFrequencies = useMemo(() => frequencyBySubscription(subscriptions), [subscriptions])

  // Never a CRM quotation: products are added, pauses booked and lockers
  // moved on this subscription (DF-381).
  const activeSubscription = useMemo(() => mainSubscription(subscriptions), [subscriptions])

  /**
   * The subscription whose delivery note is shown and edited. A customer can
   * hold several active subscriptions (different rounds), each with its own
   * note, but the page shows one field; prefer the active one that already has
   * a note, so an existing note is never hidden behind an empty first record.
   */
  const noteSubscription = useMemo(() => {
    const active = subscriptions.filter(s => s.statecode !== 1)
    return active.find(s => (s.delivery_notes ?? '').trim() !== '') ?? mainSubscription(subscriptions)
  }, [subscriptions])

  /** The soonest delivery still open to change, used to seed the dialogs. */
  const firstEditableDate = useMemo(() => {
    const next = deliveries.find(d => d.isEditable && !d.isHoliday)
    return next ? toLocalDateString(next.date) : undefined
  }, [deliveries])

  // One gate for every editing entry point, so the kill switch can't be
  // bypassed by whichever button happens to be added next.
  const navigate = useNavigate()

  const guardEditing = useCallback((open: () => void) => {
    if (ORDER_EDITING_DISABLED) {
      setIsEditingDisabledOpen(true)
      return
    }
    open()
  }, [])

  /** Active holidays as ranges the planner can reason about. */
  const holidayRanges = useMemo<HolidayRange[]>(
    () => holidayRequests
      .filter(h => h.statecode === 0 && h.start_date && h.end_date)
      .map(h => ({ id: h.id, start: toStartOfDay(h.start_date), end: toStartOfDay(h.end_date) })),
    [holidayRequests]
  )

  /** Is this YYYY-MM-DD date inside a booked holiday? (mirrors the add flow.) */
  const isDateOnHoliday = useCallback((dateStr: string): boolean => {
    const date = new Date(dateStr)
    return holidayRequests.some(
      h => h.statecode === 0 && h.start_date && h.end_date
        && date >= new Date(h.start_date) && date <= new Date(h.end_date)
    )
  }, [holidayRequests])

  /**
   * Run the CRM holiday ops for a per-day pause ('skip') or resume ('cancel').
   * Merges into neighbouring holidays where possible so a run of skips is one
   * CRM range rather than a scatter of single days. Throws on failure so callers
   * can surface the server's own reason.
   */
  const applySkipOps = useCallback(async (date: Date, mode: 'skip' | 'cancel') => {
    if (!activeSubscription?.chargebee_id) return
    const ops = mode === 'skip'
      ? planSkip(date, holidayRanges, deliveries.map(d => d.date))
      : planCancelSkip(date, holidayRanges)

    for (const op of ops) {
      if (op.type === 'create') {
        await holidayService.createHoliday(activeSubscription.chargebee_id, op.start, op.end)
      } else if (op.type === 'update') {
        await holidayService.updateHoliday(op.id, {
          ...(op.start ? { start_date: op.start } : {}),
          ...(op.end ? { end_date: op.end } : {}),
        })
      } else {
        await holidayService.cancelHoliday(op.id)
      }
    }
  }, [activeSubscription, holidayRanges, deliveries])

  /**
   * The per-day Pause / Resume on the schedule. Both just ask for
   * confirmation; the work happens in confirmSkip. A pause's end_date is the
   * RESTART date and is excluded from the paused period, so pausing one day
   * means restarting the next. Pausing a day that carries a one-off (milk
   * locker, DF-230) would quietly drop it, so we divert to the move prompt.
   */
  const lockerOnDate = useCallback((date: Date): SubscriptionLine | undefined => {
    const dayStr = toLocalDateString(date)
    return oneOffLines.find(
      l => l.start_date && toLocalDateString(new Date(l.start_date)) === dayStr
    )
  }, [oneOffLines])

  const handleSkipDay = useCallback((date: Date) => {
    guardEditing(() => {
      const locker = lockerOnDate(date)
      if (locker) {
        setMoveDate('')
        setMoveContext({ kind: 'pause-day', line: locker, date })
      } else {
        setSkipTarget({ date, mode: 'skip' })
      }
    })
  }, [guardEditing, lockerOnDate])

  const handleCancelSkip = useCallback((date: Date) => {
    guardEditing(() => setSkipTarget({ date, mode: 'cancel' }))
  }, [guardEditing])

  const confirmSkip = useCallback(async () => {
    if (!skipTarget) return

    setSkipping(true)
    try {
      await applySkipOps(skipTarget.date, skipTarget.mode)
      await handleSuccess()
      setSkipTarget(null)
    } catch (err) {
      console.error('Failed to update skip:', err)
      // Surface the server's own reason (e.g. the 5pm cutoff message) rather
      // than a generic failure, so a change loaded before 5pm and submitted
      // after gets a clear "too late" explanation (FEDEV-532).
      const serverError = err as { body?: { message?: string }; message?: string }
      setError(
        serverError?.body?.message ||
        serverError?.message ||
        'We could not update that delivery. Please try again.'
      )
      setSkipTarget(null)
    } finally {
      setSkipping(false)
    }
  }, [skipTarget, applySkipOps, handleSuccess])

  /**
   * The CRM work of the holiday calendar's staged toggles, without the UI
   * wrapping (busy flag, refresh, closing). Shared by the direct save
   * (applyHolidayChanges) and the locker-move-first path (confirmMove), which
   * applies the same deltas after moving.
   *
   * Each changed delivery is planned on its own through planSkip/planCancelSkip,
   * which merge and trim adjacent ranges so a run of skips stays one CRM record.
   * Applied in date order against holidays re-read from the server between each,
   * so two toggles touching the same booked holiday can't race to overwrite it.
   */
  const runHolidayDeltas = useCallback(async (deltas: HolidayDelta[]) => {
    if (!activeSubscription?.chargebee_id || deltas.length === 0) return
    let ranges = holidayRanges
    const deliveryDates = deliveries.map(d => d.date)
    const sorted = [...deltas].sort((a, b) => a.date.getTime() - b.date.getTime())

    for (const delta of sorted) {
      const ops = delta.skip
        ? planSkip(delta.date, ranges, deliveryDates)
        : planCancelSkip(delta.date, ranges)

      for (const op of ops) {
        if (op.type === 'create') {
          await holidayService.createHoliday(activeSubscription.chargebee_id, op.start, op.end)
        } else if (op.type === 'update') {
          await holidayService.updateHoliday(op.id, {
            ...(op.start ? { start_date: op.start } : {}),
            ...(op.end ? { end_date: op.end } : {}),
          })
        } else {
          await holidayService.cancelHoliday(op.id)
        }
      }

      const resp = await holidayService.getHolidays(50)
      ranges = resp.data
        .filter(h => h.statecode === 0 && h.start_date && h.end_date)
        .map(h => ({ id: h.id, start: toStartOfDay(h.start_date), end: toStartOfDay(h.end_date) }))
    }
  }, [activeSubscription, holidayRanges, deliveries])

  // The one-off being moved (any entry point), and the days it can move to: the
  // customer's delivery days from the add cut-off on, that the locker can reach
  // and that aren't a holiday, minus its current day and any day being paused in
  // the same action (DF-230).
  const movingLine = moveContext?.line ?? null

  const moveOptions = useMemo<OneOffEligibleDate[]>(() => {
    if (!movingLine) return []
    const item = priceItems.find(p => p.product_id === movingLine.product_id) ?? null
    const currentStr = movingLine.start_date ? toLocalDateString(new Date(movingLine.start_date)) : ''
    const staged = moveContext?.kind === 'pause-calendar' ? moveContext.excludeDates : []
    const blocked = new Set([currentStr, ...staged])
    return computeOneOffEligibleDates({
      item,
      customerDeliveryDays: user?.delivery_days ?? [],
      floorDate: cutoffs?.cutoffs.add_new_line.min_date,
      horizonDays: 56,
      isOnHoliday: isDateOnHoliday,
      exclude: d => blocked.has(d),
    })
  }, [movingLine, moveContext, priceItems, user?.delivery_days, cutoffs, isDateOnHoliday])

  // Past its cut-off, the locker can't be cancelled, so it can't be moved either.
  const lockerPastCutoff = !!movingLine?.cancel_cutoff
    && new Date() >= new Date(movingLine.cancel_cutoff)

  /** Cancel the locker on its current day and re-add it on the chosen day. */
  const moveLockerLine = useCallback(async (line: SubscriptionLine, target: OneOffEligibleDate) => {
    if (!activeSubscription) return
    const fallback = new Date()
    fallback.setDate(fallback.getDate() + 7)
    const removeFrom = cutoffs?.cutoffs.remove_line.min_date ?? toLocalDateString(fallback)
    const item = priceItems.find(p => p.product_id === line.product_id)

    await subscriptionService.createLineAmendment({
      subscription_line_id: line.id,
      quantity: 0,
      effective_from: removeFrom,
    })
    await subscriptionService.createSubscriptionLine({
      subscription_id: activeSubscription.id,
      product_id: line.product_id,
      delivery_day: target.day,
      quantity: 1,
      start_date: target.date,
      end_date: addDaysToDateString(target.date, item?.one_off_days ?? 0),
      chargebee_id: target.chargebeeId,
      product_name: line.product_name ?? item?.name ?? undefined,
      unit_price: item?.amount ?? line.unit_price,
    })
  }, [activeSubscription, cutoffs, priceItems])

  const failMove = useCallback((err: unknown) => {
    console.error('Failed to move locker:', err)
    const serverError = err as { body?: { message?: string }, message?: string }
    setError(
      serverError?.body?.message ||
      serverError?.message ||
      'We could not update that delivery. Please try again.'
    )
    setMoveContext(null)
  }, [])

  /** Move the locker to the chosen day, then carry out the pause it interrupted. */
  const confirmMove = useCallback(async () => {
    const ctx = moveContext
    const target = moveOptions.find(o => o.date === (moveDate || moveOptions[0]?.date)) ?? null
    if (!ctx || !target) return

    // Check the pause the move is tied to is itself allowed BEFORE moving. The
    // move can't be undone, so a pause the server would reject (a day before the
    // holiday cut-off) must stop us here, not strand the locker on a new day.
    const floor = cutoffs?.cutoffs.holiday_add.min_date
    const pauseDates = ctx.kind === 'pause-day'
      ? [toLocalDateString(ctx.date)]
      : ctx.kind === 'pause-calendar'
        ? ctx.deltas.filter(d => d.skip).map(d => toLocalDateString(d.date))
        : []
    if (floor && pauseDates.some(d => d < floor)) {
      setError(`The earliest day you can pause is ${formatOrdinalDate(floor)}. Please pick a later day, then move your product.`)
      setMoveContext(null)
      return
    }

    setMovingLocker(true)
    try {
      await moveLockerLine(ctx.line, target)
      if (ctx.kind === 'pause-day') await applySkipOps(ctx.date, 'skip')
      else if (ctx.kind === 'pause-calendar') await runHolidayDeltas(ctx.deltas)
      await handleSuccess()
      setMoveContext(null)
    } catch (err) {
      failMove(err)
    } finally {
      setMovingLocker(false)
    }
  }, [moveContext, moveOptions, moveDate, cutoffs, moveLockerLine, applySkipOps, runHolidayDeltas, handleSuccess, failMove])

  /**
   * Undoing a change means a different call per kind: an amendment is deleted,
   * a scheduled ending has its end date cleared, and a line that hasn't started
   * yet is removed by ending it before it begins (which is what the amendments
   * endpoint does with a quantity of zero). A pause is both at once, since it
   * is stored as an ending plus a line scheduled to follow it.
   */
  const confirmCancelChange = useCallback(async () => {
    if (!cancellingChange) return

    setCancelling(true)
    try {
      const change = cancellingChange
      if (change.kind === 'pause' && change.pausedLines?.length) {
        // One pause covers every day the product is on, so undo each day's
        // pair. Resume line first: clearing the end date first would leave the
        // original open-ended while the resume line still exists, which is two
        // live lines on the same day. A quantity of zero on a line that hasn't
        // started deletes it outright, same as the 'starting' case below.
        const fallback = new Date()
        fallback.setDate(fallback.getDate() + 7)
        const removeFrom = cutoffs?.cutoffs.remove_line.min_date ?? toLocalDateString(fallback)

        for (const pair of change.pausedLines) {
          await subscriptionService.createLineAmendment({
            subscription_line_id: pair.resumeLineId,
            quantity: 0,
            effective_from: removeFrom,
          })
          await subscriptionService.clearLineEndDate(pair.lineId)
        }
      } else if (change.kind === 'freeze' && change.freezeIds?.length) {
        // A product pause is one Line Freeze per day it's on (DF-350); cancel
        // them all so the whole pause goes, not just one day of it.
        for (const freezeId of change.freezeIds) {
          await subscriptionService.cancelFreeze(freezeId)
        }
      } else if (change.kind === 'amendment' && change.amendmentId) {
        await subscriptionService.cancelAmendment(change.amendmentId)
      } else if (change.kind === 'ending') {
        await subscriptionService.clearLineEndDate(change.lineId)
      } else {
        // Removing a line needs a date that clears the removal notice period —
        // today never does, so this used to fail every time. The backend ends a
        // not-yet-started line the day before it starts regardless of what is
        // sent here, so this only has to satisfy the cutoff.
        const fallback = new Date()
        fallback.setDate(fallback.getDate() + 7)
        const removeFrom = cutoffs?.cutoffs.remove_line.min_date ?? toLocalDateString(fallback)

        // Adding a product to two days makes two lines under one annotation,
        // so undoing it has to remove both.
        for (const lineId of change.lineIds ?? [change.lineId]) {
          await subscriptionService.createLineAmendment({
            subscription_line_id: lineId,
            quantity: 0,
            effective_from: removeFrom,
          })
        }
      }
      await handleSuccess()
      setCancellingChange(null)

      // Close the edit dialog behind the confirmation as well.
      //
      // Cancelling is reached from inside that dialog, and undoing the change
      // can leave it with nothing to act on: adding a product and then
      // cancelling that addition removes the only thing it was editing, so it
      // sat there open over a product no longer on the order. Closing on
      // success also puts the customer back on the weekly panel, which is
      // where the result of what they just did is visible.
      //
      // Only on success. A failed cancel leaves the change in place, so the
      // dialog is still showing something real and they may want to retry.
      setEditingProductId(null)
    } catch (err) {
      console.error('Failed to cancel change:', err)
      setError('We could not cancel that change. Please try again.')
      setCancellingChange(null)
    } finally {
      setCancelling(false)
    }
  }, [cancellingChange, handleSuccess, cutoffs])

  /**
   * Remove a one-off product. Unlike a recurring product there is no day or
   * date to choose: a one-off is a single delivery, so removing it just cancels
   * that one. Same call as undoing a not-yet-started line (quantity 0); the
   * backend ends it before it delivers regardless of the date sent, so this
   * only has to clear the removal cutoff.
   */
  const confirmRemoveOneOff = useCallback(async () => {
    if (!removingOneOff) return

    setRemovingOneOffBusy(true)
    try {
      const fallback = new Date()
      fallback.setDate(fallback.getDate() + 7)
      const removeFrom = cutoffs?.cutoffs.remove_line.min_date ?? toLocalDateString(fallback)

      const lines = oneOffLines.filter(l => l.product_id === removingOneOff.productId)
      for (const line of lines) {
        await subscriptionService.createLineAmendment({
          subscription_line_id: line.id,
          quantity: 0,
          effective_from: removeFrom,
        })
      }
      await handleSuccess()
      setRemovingOneOff(null)
    } catch (err) {
      console.error('Failed to remove one-off:', err)
      const serverError = err as { body?: { message?: string }, message?: string }
      setError(
        serverError?.body?.message ||
        serverError?.message ||
        'We could not remove that item. Please try again.'
      )
      setRemovingOneOff(null)
    } finally {
      setRemovingOneOffBusy(false)
    }
  }, [removingOneOff, oneOffLines, cutoffs, handleSuccess])

  /**
   * Save the holiday calendar. If one of the newly paused days carries a one-off
   * (milk locker, DF-230), stop and move it first: the move prompt then applies
   * these same changes. Otherwise apply them straight away.
   */
  const applyHolidayChanges = useCallback(async (deltas: HolidayDelta[]) => {
    if (!activeSubscription?.chargebee_id || deltas.length === 0) return

    const pausing = deltas.filter(d => d.skip)
    const lockerDelta = pausing.map(d => ({ d, line: lockerOnDate(d.date) })).find(x => x.line)
    if (lockerDelta?.line) {
      setMoveDate('')
      setMoveContext({
        kind: 'pause-calendar',
        line: lockerDelta.line,
        deltas,
        excludeDates: pausing.map(d => toLocalDateString(d.date)),
      })
      setHolidayCalendarOpen(false)
      return
    }

    setSavingHolidays(true)
    try {
      await runHolidayDeltas(deltas)

      await handleSuccess()
      setHolidayCalendarOpen(false)
      setHolidayInitialMonth(null)
    } catch (err) {
      console.error('Failed to save holidays:', err)
      setError('We could not save your holidays. Please try again.')
    } finally {
      setSavingHolidays(false)
    }
  }, [activeSubscription, runHolidayDeltas, lockerOnDate, handleSuccess])

  /** Save the client-facing delivery note to CRM, then refresh. */
  const handleSaveDriverNote = useCallback(async (note: string) => {
    if (!noteSubscription) return
    try {
      await subscriptionService.updateDeliveryNotes(noteSubscription.id, note)
      await handleSuccess()
    } catch (err) {
      console.error('Failed to save delivery notes:', err)
      setError('We could not save your delivery instructions. Please try again.')
    }
  }, [noteSubscription, handleSuccess])

  const nextDeliveryWeekday = useMemo(() => {
    const next = deliveries.find(d => d.isEditable && !d.isHoliday)
    return next ? next.date.getDay() : toStartOfDay(new Date()).getDay()
  }, [deliveries])

  if (error) {
    return (
      <div className="sun-dashboard">
        <div className="sun-error">
          <p>{error}</p>
          <button className="sun-btn" onClick={fetchSubscriptions}>Try again</button>
        </div>
      </div>
    )
  }

  // Pause deliveries on its own route: the same calendar the desktop rail opens
  // as a dialog, but rendered as a full page for the mobile bottom nav. Reuses
  // this component's deliveries and save logic; returns to My Deliveries once
  // saved (or via the bottom nav / back).
  if (view === 'pause') {
    return (
      <div className="sun-dashboard">
        {loading ? (
          <div className="sun-loading" role="status">
            <div className="spinner" aria-hidden="true" />
            <p>Loading your deliveries...</p>
          </div>
        ) : (
          <HolidayCalendarModal
            asPage
            isOpen
            onClose={() => navigate('/')}
            deliveries={deliveries}
            onSave={async (deltas) => {
              await applyHolidayChanges(deltas)
              navigate('/')
            }}
            saving={savingHolidays}
            minPauseDate={cutoffs?.cutoffs.holiday_add.min_date}
            roundAmendments={overlay.round_day_amendments}
            dateOverrides={overlay.date_overrides}
            initialMonthDate={null}
          />
        )}
      </div>
    )
  }

  return (
    <div className="sun-dashboard">
      {/* DF-478: first, as it's about the deliveries on this page. */}
      {view !== 'management' && overlay.schedule_banner && <ScheduleChangeBanner periods={periods} />}

      <DirectDebitAlert />

      <OrderBeingPreparedBanner show={awaitingFirstDelivery} />

      <div className="sun-layout">
        {view === 'management' ? (
          // Delivery Management: the same standing order and dialogs, grouped
          // by day, plus address and driver-note sections. Reuses every handler
          // already wired below, so nothing here is a second copy of a save.
          <DeliveryManagementPanel
            subscriptions={subscriptions}
            amendments={amendments}
            freezes={freezes}
            holidayRanges={holidayRanges}
            deliveryDays={user?.delivery_days ?? []}
            address={user?.address ?? null}
            loading={loading}
            editingDisabled={loading}
            onAddProduct={() => guardEditing(() => setIsAddOpen(true))}
            onViewProduct={(id, name, price) => setViewingProduct({ id, name, price })}
            onChangeQuantity={(productId, day, newQty) => guardEditing(() => setChangeConfig({ productId, intent: 'quantity', quantities: { [day]: newQty } }))}
            onPause={(productId) => guardEditing(() => setChangeConfig({ productId, intent: 'pause' }))}
            onDelete={(productId) => guardEditing(() => setChangeConfig({ productId, intent: 'remove' }))}
            oneOffLines={oneOffLines}
            onRemoveOneOff={(productId) => guardEditing(() => {
              const line = oneOffLines.find(l => l.product_id === productId)
              setRemovingOneOff({ productId, name: line?.product_name ?? 'this item' })
            })}
            onMoveOneOff={(productId) => guardEditing(() => {
              const line = oneOffLines.find(l => l.product_id === productId)
              if (line) { setMoveDate(''); setMoveContext({ kind: 'move', line }) }
            })}
            onCancelChange={setCancellingChange}
            onManageHolidays={() => guardEditing(() => { setHolidayInitialMonth(null); setHolidayCalendarOpen(true) })}
            driverNote={noteSubscription?.delivery_notes ?? ''}
            onSaveDriverNote={handleSaveDriverNote}
          />
        ) : (
          <>
            <DeliverySchedule
              deliveries={deliveries}
              loading={loading}
              onSkipDay={handleSkipDay}
              minPauseDate={cutoffs?.cutoffs.holiday_add.min_date}
              onCancelSkip={handleCancelSkip}
              skipDisabled={loading}
              onViewProduct={(id, name, price) => setViewingProduct({ id, name, price })}
              surchargeAmount={surchargeAmount}
              oneOffLines={oneOffLines}
              roundAmendments={overlay.round_day_amendments}
              glassWindows={overlay.glass_windows}
              glassOptedOut={overlay.glass_opted_out}
              dateOverrides={overlay.date_overrides}
              frequencyBySubscription={subscriptionFrequencies}
            />

            {/* Right-hand rail. The calendar stays on the schedule itself,
                behind its own show/hide, so there is one calendar and one
                place that owns which week is on screen. */}
            <div className="sun-column sun-rail">
              {/* Skipped deliveries moved to Delivery Management; My Deliveries
                  keeps just the actions. */}
              {/* The three things a customer comes here to do, in one place.
                  Buttons rather than links: each opens a dialog rather than
                  going anywhere. */}
              <nav className="sun-rail-actions" aria-label="Change your order">
                <button
                  type="button"
                  className="sun-rail-action"
                  onClick={() => guardEditing(() => setIsAddOpen(true))}
                  disabled={loading}
                >
                  <MdAddShoppingCart aria-hidden="true" />
                  Add a product
                </button>

                {/* Navigates to the Delivery Management page rather than
                    opening the change dialog inline. */}
                <Link
                  to="/delivery-management"
                  className="sun-rail-action"
                >
                  <MdEdit aria-hidden="true" />
                  Manage my Delivery
                </Link>

                <button
                  type="button"
                  className="sun-rail-action"
                  onClick={() => guardEditing(() => {
                    setHolidayInitialMonth(null)
                    setHolidayCalendarOpen(true)
                  })}
                  disabled={loading}
                >
                  <MdEventBusy aria-hidden="true" />
                  Pause delivery
                </button>
              </nav>

            </div>
          </>
        )}
      </div>

      <HolidayCalendarModal
        isOpen={holidayCalendarOpen}
        onClose={() => { setHolidayCalendarOpen(false); setHolidayInitialMonth(null) }}
        deliveries={deliveries}
        onSave={applyHolidayChanges}
        saving={savingHolidays}
        minPauseDate={cutoffs?.cutoffs.holiday_add.min_date}
        roundAmendments={overlay.round_day_amendments}
        dateOverrides={overlay.date_overrides}
        initialMonthDate={holidayInitialMonth}
      />

      {/*
        One dialog, two ways in. A product tile opens it with that product
        chosen; a delivery card's "Make changes" opens it with none, so the
        first step is picking what to change. isOpen therefore cannot key off
        editingProductId alone, or the card route would never open it.
      */}
      <ChangeItemsModal
        isOpen={editingProductId !== null || isChangesOpen || changeConfig !== null}
        onClose={() => {
          setEditingProductId(null)
          setIsChangesOpen(false)
          setChangeConfig(null)
        }}
        onSuccess={handleSuccess}
        currentItems={allLines}
        subscriptionId={activeSubscription?.id ?? ''}
        subscriptions={subscriptions}
        deliveryDay={nextDeliveryWeekday}
        defaultEffectiveDate={firstEditableDate}
        amendments={amendments}
        scheduledChanges={(() => {
          const pid = changeConfig?.productId ?? editingProductId
          return pid ? changesByProduct.get(pid) ?? [] : []
        })()}
        scheduledChangesByProduct={changesByProduct}
        onViewProduct={(id, name, price) => setViewingProduct({ id, name, price })}
        onCancelChange={setCancellingChange}
        initialProductId={changeConfig?.productId ?? editingProductId ?? undefined}
        initialIntent={changeConfig?.intent}
        initialQuantities={changeConfig?.quantities}
      />

      <AddProductModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSuccess={handleSuccess}
        subscriptionId={activeSubscription?.id ?? ''}
        billingFrequency={billingFrequencyOf(activeSubscription)}
        schedules={addSchedules(subscriptions)}
        deliveryDate={firstEditableDate}
        currentItems={allLines}
        firstDeliveryLockDate={activeSubscription?.is_awaiting_first_delivery ? activeSubscription.first_delivery_date : null}
        onChangeInstead={(productId) => {
          setIsAddOpen(false)
          setEditingProductId(productId)
        }}
      />

      <ConfirmModal
        isOpen={skipTarget !== null}
        onClose={() => setSkipTarget(null)}
        onConfirm={confirmSkip}
        message={skipTarget
          ? skipTarget.mode === 'cancel'
            ? `Would you like to resume your delivery on ${formatSkipDate(skipTarget.date)}?`
            : `Would you like to pause your delivery on ${formatSkipDate(skipTarget.date)}?`
          : ''}
        confirmText="Yes"
        cancelText="No"
        isLoading={skipping}
      />

      <MoveLockerModal
        isOpen={moveContext !== null}
        productName={movingLine?.product_name ?? 'one-off delivery'}
        lockerDayLabel={movingLine?.start_date ? formatOrdinalDate(movingLine.start_date) : ''}
        pauseNote={
          moveContext?.kind === 'pause-day'
            ? `Your delivery on ${formatOrdinalDate(toLocalDateString(moveContext.date))} is paused.`
            : moveContext?.kind === 'pause-calendar'
              ? 'Your paused days are saved.'
              : ''
        }
        pastCutoff={lockerPastCutoff}
        options={moveOptions}
        selected={moveDate || moveOptions[0]?.date || ''}
        onSelect={setMoveDate}
        onConfirm={confirmMove}
        onClose={() => setMoveContext(null)}
        isBusy={movingLocker}
      />

      <ConfirmModal
        isOpen={cancellingChange !== null}
        onClose={() => setCancellingChange(null)}
        onConfirm={confirmCancelChange}
        title="Cancel this change?"
        message={cancellingChange?.text ?? ''}
        confirmText="Yes"
        cancelText="No"
        isLoading={cancelling}
      />

      <ConfirmModal
        isOpen={removingOneOff !== null}
        onClose={() => setRemovingOneOff(null)}
        onConfirm={confirmRemoveOneOff}
        title="Remove this one-off?"
        message={`Remove your ${removingOneOff?.name ?? 'one-off item'}? It's a single delivery, so it simply won't come.`}
        confirmText="Remove"
        cancelText="Keep it"
        isDestructive
        isLoading={removingOneOffBusy}
      />

      <ProductDetailModal
        productId={viewingProduct?.id ?? null}
        fallbackName={viewingProduct?.name}
        unitPrice={viewingProduct?.price}
        deliverableDays={viewingProduct ? deliverableDaysFor(viewingProduct.id) : undefined}
        customerDeliveryDays={user?.delivery_days ?? undefined}
        onClose={() => setViewingProduct(null)}
      />

      <OrderEditingDisabledModal
        isOpen={isEditingDisabledOpen}
        onClose={() => setIsEditingDisabledOpen(false)}
      />
    </div>
  )
}

export default SunDashboard
